import * as React from 'react';

import { DEFAULT_SKILL_OPTIONS_LIMIT, SKILL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useLearnerObjectUpdateSkillsWanted from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateSkillsWanted';
import SettingRow from 'bundles/personalization-settings/components/SettingRow';
import SkillsEditor from 'bundles/personalization-settings/components/SkillsEditor';
import { usePersonalizationSettingsContext } from 'bundles/personalization-settings/contexts/PersonalizationSettingsContext';
import useSettingSave from 'bundles/personalization-settings/hooks/useSettingSave';
import useSkillsCatalogSearch from 'bundles/personalization-settings/hooks/useSkillsCatalogSearch';
import type { SelectedSkill } from 'bundles/personalization-settings/utils/skillsWantedV2';

import _t from 'i18n!nls/personalization-settings';

export type SkillsSettingItemProps = {
  label: string;
};

/**
 * The "Desired skills" row: the label in the left column and the editable
 * {@link SkillsEditor} on the right.
 *
 * This is the editor's data container: it loads the catalog of selectable skills
 * as the learner types (via {@link useSkillsCatalogSearch}, the same Skills
 * search the onboarding picker uses) and passes them down, keeping SkillsEditor
 * presentational.
 */
const SkillsSettingItem = ({ label }: SkillsSettingItemProps) => {
  const { values, loading: readLoading, setFieldValue } = usePersonalizationSettingsContext();
  const savedSkills = values.skills;
  const { updateSkillsWanted } = useLearnerObjectUpdateSkillsWanted();

  const [input, setInput] = React.useState('');
  const { availableSkills, loading: catalogLoading } = useSkillsCatalogSearch(input);

  const seededValue = React.useMemo<SelectedSkill[]>(() => {
    if (!savedSkills) return [];
    return savedSkills.flatMap((skill): SelectedSkill[] => {
      if (skill.type === SKILL_OPTIONS_TYPE.DEFAULT)
        return [
          {
            type: 'catalog',
            name: skill.skillName,
            id: skill.id,
          },
        ];
      if (skill.type === SKILL_OPTIONS_TYPE.CUSTOM)
        return [
          {
            type: 'userGenerated',
            name: skill.skillName,
          },
        ];
      return [];
    });
  }, [savedSkills]);

  const {
    value: editorValue,
    notificationType,
    onChange: handleChange,
  } = useSettingSave(
    seededValue,
    async (skills) => {
      const skillsWanted = skills.map((skill) =>
        skill.type === 'catalog'
          ? {
              id: skill.id,
              type: SKILL_OPTIONS_TYPE.DEFAULT,
              skillName: skill.name,
            }
          : {
              id: skill.name.toLowerCase(),
              skillName: skill.name,
              type: SKILL_OPTIONS_TYPE.CUSTOM,
            }
      );
      const result = await updateSkillsWanted(skillsWanted);
      if (result.success) {
        setFieldValue('skills', skillsWanted.length > 0 ? skillsWanted : null);
      }
      return result;
    },
    'skills'
  );

  // Surfaces an attempt to add past the limit as a validation error in the same slot
  // as the save notification. A learner at the limit can only remove a chip, not add
  // one, so clearing on the next add-or-remove would never fire -- clear on blur
  // instead, since it's learner-driven and doesn't require giving anything up.
  const [overLimitAttempts, setOverLimitAttempts] = React.useState(0);

  return (
    <SettingRow
      label={label}
      notificationType={overLimitAttempts > 0 ? 'error' : notificationType}
      notificationMessage={
        overLimitAttempts > 0
          ? _t('You have reached the limit of #{skillsLimit} skills', { skillsLimit: DEFAULT_SKILL_OPTIONS_LIMIT })
          : undefined
      }
      notificationKey={overLimitAttempts}
      loading={readLoading}
    >
      <SkillsEditor
        value={editorValue}
        onChange={handleChange}
        availableSkills={availableSkills}
        onInputChange={setInput}
        loading={catalogLoading}
        onLimitExceededAttempt={() => setOverLimitAttempts((count) => count + 1)}
        onBlur={() => setOverLimitAttempts(0)}
      />
    </SettingRow>
  );
};

export default SkillsSettingItem;
